/**
 * Single source of truth for estimate line pricing, shared by the editor
 * totals (estimateTotals.js) and the customer quote (projectQuoteSnapshots.js)
 * so they can no longer disagree (audit finding F01).
 *
 * Materials catalog (public.materials): unit_cost = our cost (internal),
 * sell_price = client price. Estimate lines freeze them as unit_cost_snapshot /
 * sell_price_snapshot. The customer price of a line is:
 *   sell_price_snapshot → live catalog sell_price → cost (fallback, so a line
 *   without a client price is never quoted at $0).
 * A per-line override ("Your price") then applies: amount = fixed unit price,
 * percent = +/- % on the unit price.
 */

export const PRICE_OVERRIDE_MODE = Object.freeze({ AMOUNT: "amount", PERCENT: "percent" });

const num = (v) => (v == null || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);

export function roundCents(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/** Our cost per unit (internal only). */
export function getMaterialOurCost(row, materialsCatalog = []) {
  if (row?.material_id != null) {
    const item = (materialsCatalog || []).find((m) => m.material_id === row.material_id);
    const live = num(item?.unit_cost);
    if (live != null) return live;
  }
  return num(row?.unit_cost_snapshot) ?? 0;
}

/** Catalog client price per unit (before any line override). */
export function getMaterialListPrice(row, materialsCatalog = []) {
  const frozen = num(row?.sell_price_snapshot);
  if (frozen != null) return frozen;
  if (row?.material_id != null) {
    const item = (materialsCatalog || []).find((m) => m.material_id === row.material_id);
    const live = num(item?.sell_price);
    if (live != null) return live;
  }
  return getMaterialOurCost(row, materialsCatalog);
}

/** Override result for a unit price. Returns the unit price unchanged when no override. */
export function applyPriceOverride(unitPrice, mode, value) {
  const v = num(value);
  if (!mode || v == null) return Number(unitPrice) || 0;
  if (mode === PRICE_OVERRIDE_MODE.AMOUNT) return Math.max(0, v);
  if (mode === PRICE_OVERRIDE_MODE.PERCENT) return Math.max(0, roundCents((Number(unitPrice) || 0) * (1 + v / 100)));
  return Number(unitPrice) || 0;
}

export function hasPriceOverride(row) {
  return Boolean(row?.price_override_mode) && num(row?.price_override_value) != null;
}

/** Customer unit price for a material line (override applied). */
export function getMaterialClientUnitPrice(row, materialsCatalog = []) {
  return applyPriceOverride(getMaterialListPrice(row, materialsCatalog), row?.price_override_mode, row?.price_override_value);
}

/** Customer rate for a labour line (override applied). The entered rate is the price. */
export function getLabourClientRate(row) {
  const rate = num(row?.hourly_rate) ?? 0;
  return applyPriceOverride(rate, row?.price_override_mode, row?.price_override_value);
}

/** Markup helpers for the catalog editor: client price from cost + markup %, and back. */
export function priceFromMarkup(cost, markupPct) {
  return roundCents((Number(cost) || 0) * (1 + (Number(markupPct) || 0) / 100));
}
export function markupFromPrice(cost, price) {
  const c = Number(cost) || 0;
  if (!c) return null;
  return Math.round(((Number(price) - c) / c) * 1000) / 10;
}

/* ---------------- Areas, deductions and per-surface labour (pass 2) ---------------- */

const isDeduction = (s) => s?.record_kind === "deduction";
const sqft = (s) => Number(s?.area_sqft) || 0;

/**
 * Per-area breakdown for one feature:
 *   [{ section, gross, deductions: [section…], legacyShare, net }]
 * Linked deductions (applies_to_section_id) come off their area. Legacy
 * deductions (no link) keep working by surface type: their area is shared
 * across that surface type's areas in proportion to gross area, so the sum of
 * nets still equals the trigger-maintained surface_totals.
 */
export function computeAreaBreakdown(workType) {
  const sections = workType?.sections || [];
  const areas = sections.filter((s) => !isDeduction(s));
  const deductions = sections.filter(isDeduction);
  const areaIds = new Set(areas.map((a) => a.section_id));
  const legacyBySurface = {};
  deductions.forEach((d) => {
    if (d.applies_to_section_id && areaIds.has(d.applies_to_section_id)) return;
    const key = d.applies_to_surface_type || "floor";
    legacyBySurface[key] = (legacyBySurface[key] || 0) + sqft(d);
  });
  const grossBySurface = {};
  areas.forEach((a) => {
    grossBySurface[a.surface_type] = (grossBySurface[a.surface_type] || 0) + sqft(a);
  });
  return areas.map((area) => {
    const linked = deductions.filter((d) => d.applies_to_section_id === area.section_id);
    const gross = sqft(area);
    const surfaceGross = grossBySurface[area.surface_type] || 0;
    const legacyShare = surfaceGross ? ((legacyBySurface[area.surface_type] || 0) * gross) / surfaceGross : 0;
    const net = Math.max(0, gross - linked.reduce((s, d) => s + sqft(d), 0) - legacyShare);
    return { section: area, gross, deductions: linked, legacyShare, net };
  });
}

/** Net sq ft billed by a per sq ft labour line, or null when it uses the whole feature (legacy). */
export function computeLabourSurfaceSqft(row, workType) {
  const ids = Array.isArray(row?.surface_section_ids) ? row.surface_section_ids.filter(Boolean) : [];
  if (!ids.length) return null;
  return computeAreaBreakdown(workType)
    .filter((a) => ids.includes(a.section.section_id))
    .reduce((s, a) => s + a.net, 0);
}
